/**
 * Lawyer-reviewed words every client shows (2026-10-21 Riley): one source,
 * served to the apps by GET /api/app/config as `copy` (app-config-routes.js)
 * and imported by the web at build time (Settings.tsx, PrivacyOffSheet.tsx,
 * ConsentTerms.tsx, DeleteAccountModal.tsx, LegalUpdateNotice.tsx) and by the
 * guest call page (collect-routes.js).
 * The apps carry this same text compiled in as their defaults and take the
 * served copy over it, so a change here reaches installs in people's hands
 * without a release, and an old server still shows the right words.
 *
 * Keys stay as they are — the apps decode by them; `version` is the date of
 * the last legal change to any string here (counsel's reply of 2026-10-14,
 * landed 2026-10-15 in 0a51eb911: what informs Ember is "what Ember writes
 * from what you tell", and it is "your experience", not "your Embers", that
 * a turned-off switch keeps out; 2026-10-28: background.help no longer says
 * it helps find people for a room — rooms by situation retired 2026-10-26).
 * Paragraphs and lines are arrays: the web
 * renders one <p> / <span> each, the apps one Text each.
 *
 * legal.background — Settings › Help Ember understand similar situations:
 *   on / off     the switch's sub-line, all of it, by the switch's state
 *   help         what the ? opens
 *   off_sheet    the word before it goes off: title, body, confirm (= turns
 *                it off), cancel (= keeps it on)
 *
 * The rest (2026-10-21 Riley), each moved verbatim from the client that
 * showed it — where the web and the apps differed, the web's words:
 *
 * legal.consent — the boxes before a door opens. `age` is the one line every
 *   door asks; the Terms sentence differs by door, so each door has its own,
 *   in parts a client can put two links into:
 *     before + [terms_label → terms_href] + between + [privacy_label →
 *     privacy_href] + after
 *   talk      the telling doors (talk sheet, the home composer)
 *   register  the registration sheet and /auth (the texts are part of it)
 *   call      the guest call page (the recording is part of it)
 *   app       the apps' sign-in screen and its consent sheet
 * legal.share — Settings › Another person reading your Ember: on / off,
 *   help, off_sheet, the same shape as background.
 * legal.data_controls — Settings › Data controls, what each ? opens:
 *   model_training.help, product_improvement.help.
 * legal.delete_account — the Delete your account? dialog: title, body.
 * legal.legal_update — the update notice's sentence; `{date}` is the date
 *   the Terms take effect, written out ("October 15, 2026").
 */

const BACKGROUND_DETAIL = [
  'When this is on, what Ember writes from what you tell, with identifying details removed, may inform what Ember says to people in a similar place, in conversation and on a light’s public page.',
  'This does not let anyone read your Ember. Whether people it can help may read one is the switch above, and Ember asks you about each one.',
];

const SHARE_DETAIL = 'Nobody reads your Ember unless you say yes. Ember asks about one Ember at a time, and only when it found something in it that could help someone.';

/** The Terms sentence's two links, the same in every door's. */
const TERMS_LINKS = {
  terms_label: 'Terms of Service',
  terms_href: '/terms',
  privacy_label: 'Privacy Policy',
  privacy_href: '/privacy',
};

/** One door's Terms sentence: before + Terms + between + Privacy + after. */
function termsSentence(after) {
  return { before: 'I agree to the ', ...TERMS_LINKS, between: ' and ', after };
}

export const CLIENT_COPY = Object.freeze({
  version: '2026-10-28',
  legal: {
    background: {
      on: [...BACKGROUND_DETAIL, 'Turning this off keeps your experience out of that from now on.'],
      off: [...BACKGROUND_DETAIL, 'Off. Ember doesn’t draw on your experience for anyone else.'],
      help: [
        'Ember understands a situation from the people who have lived it. With this on, you are among them: when Ember talks with someone in a similar place, or answers a question on a light’s public page, it may draw on what you went through, with your name, places and anything that could point to you left out.',
        'No one reads your words this way, and nothing is quoted. Turn it off and your experience stays out of all of that from then on. To keep just one out, use the switch on that Ember’s own page.',
      ],
      off_sheet: {
        title: 'Before you turn this off',
        body: [
          'Ember exists so that what one person has lived through can quietly help another. When Ember talks with someone in a situation like one of yours, or answers a question at a light, it may draw on what you went through, with your name, places and anything that could point to you left out.',
          'No one reads your words this way. Ember only carries the shape of what you lived, like someone who has heard many stories. Nothing of yours is posted, sold or handed anywhere.',
          'Turning this off keeps your experience out of that from now on. If it’s one Ember you’d rather keep out, you can do that from its own page instead.',
        ],
        confirm: 'Turn it off',
        cancel: 'Keep it on',
      },
    },
    consent: {
      age: 'I’m 18 or older',
      talk: termsSentence(''),
      register: termsSentence(', and to receive transactional and marketing texts'),
      call: termsSentence(', and to this call being recorded'),
      app: termsSentence(', and to how Ember handles what I tell it.'),
    },
    share: {
      on: [SHARE_DETAIL, 'Turning this off stops the asking and the offering. Each Ember you said yes to has its own switch on its page.'],
      off: [SHARE_DETAIL, 'Off. Ember doesn’t ask about your Embers and doesn’t offer them.'],
      help: [
        'When Ember finds something in one of your Embers that could genuinely help someone, it asks you first. With this on, the Embers you said yes to may be shown to a person standing where you stood — here on Ember, never who you are.',
        'Turn it off and Ember stops asking and stops offering; anyone you’ve already let in keeps reading until you close it on the Ember’s page.',
      ],
      off_sheet: {
        title: 'Before you turn this off',
        body: [
          'Ember exists so that what one person has lived through can quietly help another — the way a word from someone who has been there helps more than any advice.',
          'Your Embers are yours. Nobody reads one unless you say yes. When Ember finds something in one that could genuinely help someone, it asks you first; only the Embers you say yes to may be shown to a person standing where you stood — here on Ember, never who you are. Never a post, never a feed, never sold or handed anywhere.',
          'Turning this off means Ember won’t ask about any of your Embers and won’t offer them. What people were already given stays until you close it on that Ember’s page.',
        ],
        confirm: 'Turn it off',
        cancel: 'Keep it on',
      },
    },
    data_controls: {
      model_training: {
        help: ['Ember learns to listen and write better from real conversations. With this on, what you tell Ember may be part of that learning. It never makes your words readable to another person, and you can turn it off any time.'],
      },
      product_improvement: {
        help: ['This is about how the app is used — which pages are opened, where people get stuck — not about what you say. It helps us fix what is confusing. Turn it off and your visits are left out of that.'],
      },
    },
    delete_account: {
      title: 'Delete your account?',
      body: [
        "This schedules everything you've shared with Ember for deletion.",
        "Your data is kept for 30 days, and Ember pauses — nothing new gets written down. Signing back in won't undo the deletion on its own: to keep the account you have to press Restore account, or reply RESTORE to Ember. After the 30 days, it's gone for good.",
      ],
    },
    legal_update: {
      body: 'Our updated policies take effect on {date}. By continuing to use Ember on or after that date, you agree to the updated Terms of Use and Privacy Policy.',
    },
  },
});
